/**
 * Kreator — dane próbkowe (makieta) do podglądu "na żywo" kartoteki modułu.
 *
 * Moduł jeszcze NIE jest zainstalowany u żadnego tenanta w chwili projektowania
 * w Kreatorze — nie ma backendu ani żadnych realnych rekordów do pokazania.
 * Ten generator tworzy garstkę SYNTETYCZNYCH wierszy po stronie klienta (bez
 * sieci), typowanych zgodnie z definicją pola (`ModuleTemplateFieldDefinition`),
 * żeby formularz i lista dały się zobaczyć "jak u klienta" jeszcze przed
 * instalacją.
 *
 * Deterministyczne (indeks-based, bez `Math.random()`) — testowalny output.
 *
 * @see packages/shared/src/module-builder.ts (ModuleTemplateFieldDefinition)
 */

import type { ModuleTemplateFieldDefinition } from '@saas/shared';

/** Domyślna liczba wierszy próbkowych generowanych dla podglądu. */
export const DEFAULT_PREVIEW_RECORD_COUNT = 5;

/**
 * Generyczny string próbkowy — celowo NIE domenowy. Ten sam wzorzec dla każdego
 * pola tekstowego/referencji w każdej kartotece (setki formularzy = zero danych
 * „szytych na miarę" do utrzymania). Indeks daje rozróżnialne wiersze na liście.
 */
function exampleString(rowIndex: number): string {
  return `Example ${rowIndex + 1}`;
}

function pick<T>(pool: readonly T[], index: number): T {
  // Pool nie może być pusty w praktyce (opcje pola) — nieużywalny fallback defensywny.
  return pool[index % pool.length] as T;
}

/** Data w formacie `YYYY-MM-DD`, przesunięta o `offsetDays` względem dziś. */
function sampleDate(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return d.toISOString().slice(0, 10);
}

/** Znacznik czasu ISO 8601, przesunięty o `offsetDays` względem teraz. */
function sampleDatetime(offsetDays: number): string {
  const d = new Date();
  d.setDate(d.getDate() - offsetDays);
  return d.toISOString();
}

/** Wartość dla pojedynczego pola, w zależności od typu — bazowana na indeksie wiersza. */
function sampleValueForField(
  field: ModuleTemplateFieldDefinition,
  rowIndex: number,
): string | number | boolean | string[] | null {
  switch (field.type) {
    case 'text':
      return exampleString(rowIndex);
    case 'longtext':
      return exampleString(rowIndex);
    case 'number':
      return 10 + rowIndex * 7;
    case 'decimal':
      // Format zgodny z tym, jak Drizzle `numeric` wraca z API — string.
      return (1000 + rowIndex * 250.5).toFixed(2);
    case 'date':
      return sampleDate(rowIndex * 3);
    case 'datetime':
      return sampleDatetime(rowIndex * 2);
    case 'boolean':
      return rowIndex % 2 === 0;
    case 'select': {
      const options = field.options ?? [];
      if (options.length === 0) return null;
      // Cykl po opcjach — przy count >= options.length wartości rozkładają się
      // na WSZYSTKIE opcje (istotne dla podglądu Kanban grupowanego po tym polu).
      return pick(options, rowIndex).value;
    }
    case 'multi_select': {
      const options = field.options ?? [];
      if (options.length === 0) return [];
      if (options.length === 1) return [options[0]!.value];
      // Bierzemy 2 sąsiadujące opcje (cyklicznie), przesunięte o rowIndex.
      const first = pick(options, rowIndex);
      const second = pick(options, rowIndex + 1);
      return first.value === second.value ? [first.value] : [first.value, second.value];
    }
    case 'reference':
      // Syntetyczny string wyświetlany — NIE realny id (brak backendu do rozwiązania).
      return exampleString(rowIndex);
    default:
      return null;
  }
}

/**
 * Generuje `count` syntetycznych wierszy dla podanych pól encji szablonu.
 * Każdy wiersz ma `id` (`preview-<i>`), `record_name` (etykieta makietowa)
 * oraz po jednej wartości na każde pole, kluczowanej slugiem pola.
 *
 * Deterministyczne — dla tych samych `fields`/`count` zawsze ten sam wynik.
 */
export function generateSampleRecords(
  fields: ModuleTemplateFieldDefinition[],
  count: number = DEFAULT_PREVIEW_RECORD_COUNT,
): Array<Record<string, unknown>> {
  const rows: Array<Record<string, unknown>> = [];
  for (let i = 0; i < count; i++) {
    const row: Record<string, unknown> = {
      id: `preview-${i}`,
      record_name: exampleString(i),
    };
    for (const field of fields) {
      row[field.slug] = sampleValueForField(field, i);
    }
    rows.push(row);
  }
  return rows;
}
